import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { adminListSentEmails, adminReadSentEmail } from "@/lib/api/sent-emails.functions";
import { Loader2, Mail, Search, Eye, X, Paperclip } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/admin/sent-emails")({
  head: () => ({
    meta: [
      { title: "Sent emails audit — Admin | Nkadimeng Danny Legal & Compliance" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SentEmailsPage,
});

function SentEmailsPage() {
  const list = useServerFn(adminListSentEmails);
  const [search, setSearch] = useState("");
  const [template, setTemplate] = useState("");
  const [status, setStatus] = useState("");
  const [days, setDays] = useState(30);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [onlyAttachments, setOnlyAttachments] = useState(false);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin-sent-emails", search, template, status, days],
    queryFn: () =>
      list({
        data: {
          search: search || undefined,
          template: template || undefined,
          status: status || undefined,
          since_days: days,
          limit: 300,
        } as never,
      }),
  });

  const allRows = data?.rows ?? [];
  const rows = onlyAttachments
    ? allRows.filter((r: any) => (r.attachments?.length ?? 0) > 0)
    : allRows;
  const templates = data?.templates ?? [];
  const statuses = data?.statuses ?? [];
  const counts = data?.counts ?? { total: 0, delivered: 0, failed: 0, pending: 0 };

  const statusColor = useMemo(
    () => (s: string) => {
      if (s === "sent" || s === "delivered") return "text-emerald-600";
      if (s === "failed" || s === "dlq") return "text-red-600";
      if (s === "pending" || s === "queued") return "text-amber-600";
      return "text-muted-foreground";
    },
    [],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl text-navy flex items-center gap-2">
          <Mail className="h-6 w-6" /> Sent emails audit
        </h1>
        <p className="text-sm text-muted-foreground">
          Every email the platform has sent — auth confirmations, receipts, contact replies,
          learner invites, and partner notifications. A mirrored copy of every user-facing email
          is also delivered to your admin inbox and logged here as <code>*__admin_copy</code>.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Click <strong>View</strong> on any row to open the fully rendered email exactly as it
          was sent to the recipient.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total" value={counts.total} />
        <StatCard label="Delivered" value={counts.delivered} tone="emerald" />
        <StatCard label="Pending" value={counts.pending} tone="amber" />
        <StatCard label="Failed" value={counts.failed} tone="red" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search recipient email…"
            className="rounded-md border border-input bg-background py-2 pl-8 pr-3 text-sm"
          />
        </div>
        <select
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">All templates</option>
          {templates.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          {statuses.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 180 days</option>
          <option value={365}>Last year</option>
        </select>
        <label className="flex items-center gap-2 rounded-md border border-input px-3 py-2 text-sm">
          <input
            type="checkbox"
            checked={onlyAttachments}
            onChange={(e) => setOnlyAttachments(e.target.checked)}
          />
          With attachments only
        </label>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="rounded-md border border-input px-3 py-2 text-sm disabled:opacity-40"
        >
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Sent</th>
              <th className="px-3 py-2">Recipient</th>
              <th className="px-3 py-2">Subject / template</th>
              <th className="px-3 py-2">Attachments</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Error / notes</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                  <Loader2 className="inline h-4 w-4 animate-spin" /> Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                  No emails match these filters in the selected window.
                </td>
              </tr>
            ) : (
              rows.map((r: any) => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">{r.recipient_email}</td>
                  <td className="px-3 py-2 text-xs max-w-[260px]">
                    <div className="truncate">{r.subject ?? "—"}</div>
                    <div className="text-[11px] text-muted-foreground">{r.template_name ?? "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.attachments?.length ? (
                      <div className="space-y-0.5">
                        {r.attachments.map((name: string, i: number) => (
                          <div key={`${r.id}-att-${i}`} className="flex items-center gap-1">
                            <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
                            <span className="truncate max-w-[180px]" title={name}>{name}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className={`px-3 py-2 text-xs font-semibold ${statusColor(r.status)}`}>
                    {r.status ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground max-w-[320px]">
                    {r.error ? <span className="text-red-600">{String(r.error).slice(0, 240)}</span> : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setViewingId(r.message_id)}
                      disabled={!r.message_id}
                      className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs disabled:opacity-40"
                    >
                      <Eye className="h-3 w-3" /> View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {viewingId ? (
        <EmailPreviewModal messageId={viewingId} onClose={() => setViewingId(null)} />
      ) : null}
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number; tone?: "emerald" | "amber" | "red" }) {
  const c =
    tone === "emerald" ? "text-emerald-600"
    : tone === "amber" ? "text-amber-600"
    : tone === "red" ? "text-red-600"
    : "text-navy";
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold ${c}`}>{value}</div>
    </div>
  );
}

function EmailPreviewModal({ messageId, onClose }: { messageId: string; onClose: () => void }) {
  const read = useServerFn(adminReadSentEmail);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-sent-email", messageId],
    queryFn: () => read({ data: { message_id: messageId } as never }),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog">
      <div className="relative w-full max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-navy">
              {data?.subject ?? "Loading email…"}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              To: {data?.recipient_email ?? "—"} · Template: {data?.template_name ?? "—"} · Status: {data?.status ?? "—"}
              {data?.copy_of_recipient ? ` · Mirror of email to ${data.copy_of_recipient}` : ""}
            </div>
            {data?.attachments?.length ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {data.attachments.map((name: string, i: number) => {
                  const link = (data.attachment_links as any[])?.find((l) => l?.name === name);
                  return link?.url ? (
                    <a
                      key={`att-${i}`}
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs hover:bg-muted"
                    >
                      <Paperclip className="h-3 w-3" /> {name}
                    </a>
                  ) : (
                    <span
                      key={`att-${i}`}
                      className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs text-muted-foreground"
                    >
                      <Paperclip className="h-3 w-3" /> {name}
                    </span>
                  );
                })}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="h-[70vh] bg-muted/30">
          {isLoading ? (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading rendered email…
            </div>
          ) : error ? (
            <div className="p-6 text-sm text-red-600">
              Could not load email: {(error as Error).message}
            </div>
          ) : data?.html ? (
            <iframe
              title="Rendered email"
              sandbox=""
              srcDoc={data.html}
              className="h-full w-full bg-white"
            />
          ) : (
            <div className="p-6 text-sm text-muted-foreground">
              No rendered HTML was captured for this message.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
